import { useState, useCallback, useEffect } from 'react';
import {
  SavedSearch,
  SearchSuggestion,
  AdvancedSearchQuery,
} from '../utils/schemas';

interface UseAdvancedSearchOptions {
  entityType: 'users' | 'organizations' | 'audit-logs';
  onSearch?: (query: AdvancedSearchQuery) => void;
}

export const useAdvancedSearch = ({
  entityType,
  onSearch,
}: UseAdvancedSearchOptions) => {
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const storageKey = `savedSearches_${entityType}`;
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      try {
        setSavedSearches(JSON.parse(stored));
      } catch (error) {
        console.error('Error loading saved searches:', error);
      }
    }
  }, [entityType]);

  useEffect(() => {
    const storageKey = `savedSearches_${entityType}`;
    localStorage.setItem(storageKey, JSON.stringify(savedSearches));
  }, [savedSearches, entityType]);

  const saveSavedSearch = useCallback(
    (search: Omit<SavedSearch, 'id' | 'createdAt'>) => {
      const newSearch: SavedSearch = {
        ...search,
        id: Date.now().toString(),
        createdAt: new Date().toISOString(),
      };
      setSavedSearches((prev) => [...prev, newSearch]);
    },
    []
  );

  const deleteSavedSearch = useCallback((id: string) => {
    setSavedSearches((prev) => prev.filter((search) => search.id !== id));
  }, []);

  const updateSavedSearchUsage = useCallback((id: string) => {
    setSavedSearches((prev) =>
      prev.map((search) =>
        search.id === id
          ? { ...search, lastUsed: new Date().toISOString() }
          : search
      )
    );
  }, []);

  const getSuggestions = useCallback(
    async (query: string): Promise<SearchSuggestion[]> => {
      setIsLoading(true);

      try {
        // Mock suggestions - in a real app, this would call an API
        const suggestions: SearchSuggestion[] = [];

        if (query.includes(':')) {
          const [field, value] = query.split(':', 2);
          if (field && value) {
            suggestions.push({
              type: 'field',
              value: query,
              label: `${field}: ${value}`,
              context: 'Field search',
            });
          }
        }

        if (query.includes('@')) {
          suggestions.push({
            type: 'email',
            value: query,
            label: query,
            context: 'Email search',
          });
        }

        switch (entityType) {
          case 'users':
            if (
              query.toLowerCase().includes('free') ||
              query.toLowerCase().includes('starter') ||
              query.toLowerCase().includes('creator') ||
              query.toLowerCase().includes('pro') ||
              query.toLowerCase().includes('studio')
            ) {
              suggestions.push({
                type: 'user',
                value: query,
                label: `Users with ${query} plan`,
                context: 'Subscription plan',
              });
            }
            break;

          case 'organizations':
            if (
              query.toLowerCase().includes('team') ||
              query.toLowerCase().includes('personal')
            ) {
              suggestions.push({
                type: 'organization',
                value: query,
                label: `${query} organizations`,
                context: 'Organization type',
              });
            }
            break;

          case 'audit-logs':
            if (
              query.toLowerCase().includes('credit') ||
              query.toLowerCase().includes('user') ||
              query.toLowerCase().includes('organization')
            ) {
              suggestions.push({
                type: 'field',
                value: query,
                label: `${query} actions`,
                context: 'Action type',
              });
            }
            break;
        }

        const recentSearches = savedSearches
          .filter(
            (search) =>
              search.name.toLowerCase().includes(query.toLowerCase()) ||
              search.query.globalSearch
                ?.toLowerCase()
                .includes(query.toLowerCase())
          )
          .slice(0, 3)
          .map((search) => ({
            type: 'field' as const,
            value: search.query.globalSearch || search.name,
            label: search.name,
            context: 'Saved search',
          }));

        suggestions.push(...recentSearches);

        return suggestions.slice(0, 8);
      } catch (error) {
        console.error('Error generating suggestions:', error);
        return [];
      } finally {
        setIsLoading(false);
      }
    },
    [entityType, savedSearches]
  );

  const convertQueryToApiParams = useCallback((query: AdvancedSearchQuery) => {
    const params: Record<string, any> = {};

    if (query.globalSearch) {
      params.search = query.globalSearch;
    }

    if (query.dateRange) {
      if (query.dateRange.start) {
        params.startDate = query.dateRange.start;
      }
      if (query.dateRange.end) {
        params.endDate = query.dateRange.end;
      }
    }

    // Simplified filter-group handling; a real implementation would be more sophisticated.
    query.filterGroups.forEach((group, groupIndex) => {
      group.conditions.forEach((condition, conditionIndex) => {
        const key = `${condition.field}_${groupIndex}_${conditionIndex}`;

        switch (condition.operator) {
          case 'equals':
            params[condition.field] = condition.value;
            break;
          case 'contains':
            params[`${condition.field}_contains`] = condition.value;
            break;
          case 'greaterThan':
            params[`${condition.field}_gt`] = condition.value;
            break;
          case 'lessThan':
            params[`${condition.field}_lt`] = condition.value;
            break;
          case 'between':
            if (
              Array.isArray(condition.value) &&
              condition.value.length === 2
            ) {
              params[`${condition.field}_gte`] = condition.value[0];
              params[`${condition.field}_lte`] = condition.value[1];
            }
            break;
          case 'in':
            if (Array.isArray(condition.value)) {
              params[`${condition.field}_in`] = condition.value.join(',');
            }
            break;
        }
      });
    });

    return params;
  }, []);

  const executeSearch = useCallback(
    (query: AdvancedSearchQuery) => {
      const apiParams = convertQueryToApiParams(query);
      onSearch?.(query);

      const matchingSavedSearch = savedSearches.find(
        (search) => JSON.stringify(search.query) === JSON.stringify(query)
      );
      if (matchingSavedSearch) {
        updateSavedSearchUsage(matchingSavedSearch.id);
      }
    },
    [convertQueryToApiParams, onSearch, savedSearches, updateSavedSearchUsage]
  );

  return {
    savedSearches: savedSearches.sort((a, b) => {
      // Sort by last used, then by created date
      if (a.lastUsed && b.lastUsed) {
        return new Date(b.lastUsed).getTime() - new Date(a.lastUsed).getTime();
      }
      if (a.lastUsed && !b.lastUsed) return -1;
      if (!a.lastUsed && b.lastUsed) return 1;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    }),
    saveSavedSearch,
    deleteSavedSearch,
    getSuggestions,
    executeSearch,
    convertQueryToApiParams,
    isLoading,
  };
};
